import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2, Download, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 8;

export default function Payment() {
  const params = new URLSearchParams(window.location.search);
  const sessionId = params.get("session_id");
  const cancelled = params.get("cancel");

  const [state, setState] = useState(cancelled ? "cancelled" : "checking");
  const [invoiceNumber, setInvoiceNumber] = useState(null);

  const poll = useCallback(async (attempt) => {
    try {
      const { data } = await axios.get(`${API}/payment/confirm/${sessionId}`);
      if (data.payment_status === "paid") {
        setInvoiceNumber(data.invoice_number);
        setState("paid");
        return;
      }
      if (data.status === "expired") { setState("failed"); return; }
      if (attempt >= MAX_POLLS) { setState("pending"); return; }
      setTimeout(() => poll(attempt + 1), 2000);
    } catch {
      if (attempt >= MAX_POLLS) { setState("failed"); return; }
      setTimeout(() => poll(attempt + 1), 2000);
    }
  }, [sessionId]);

  useEffect(() => {
    if (cancelled) return;
    if (!sessionId) { setState("failed"); return; }
    poll(0);
  }, [sessionId, cancelled, poll]);

  const downloadPdf = () => {
    window.open(`${API}/payment/invoice/${sessionId}/pdf`, "_blank");
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center px-4" data-testid="payment-page">
      <div className="w-full max-w-md text-center">
        <div className="inline-flex items-center gap-2 mb-10">
          <img src="/logo-mark.png" alt="SauberEko" className="h-9 w-9" />
          <span className="font-display text-2xl tracking-wide">SauberEko<span className="text-[#D4AF37]">®</span></span>
        </div>

        {state === "checking" && (
          <div data-testid="payment-checking" className="space-y-4">
            <Loader2 className="mx-auto animate-spin text-[#D4AF37]" size={44} />
            <h1 className="font-display text-2xl">Overujeme platbu…</h1>
            <p className="text-white/50 text-sm">Moment prosím, potvrdzujeme transakciu.</p>
          </div>
        )}

        {state === "paid" && (
          <div data-testid="payment-success" className="space-y-5">
            <CheckCircle2 className="mx-auto text-green-400" size={56} />
            <h1 className="font-display text-3xl">Platba prijatá</h1>
            <p className="text-white/60">
              Ďakujeme. Vaša platba bola úspešne spracovaná{invoiceNumber ? <> a vystavili sme faktúru č. <span className="text-[#D4AF37]">{invoiceNumber}</span></> : ""}.
              Kópiu sme vám zaslali aj e-mailom.
            </p>
            <button onClick={downloadPdf} data-testid="payment-download-pdf"
              className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black font-semibold px-6 py-3 hover:bg-[#F2E3C6] transition-colors">
              <Download size={17} /> Stiahnuť faktúru (PDF)
            </button>
          </div>
        )}

        {state === "pending" && (
          <div data-testid="payment-pending" className="space-y-5">
            <Loader2 className="mx-auto animate-spin text-[#D4AF37]" size={44} />
            <h1 className="font-display text-2xl">Spracúvame platbu</h1>
            <p className="text-white/60 text-sm">
              Platba sa ešte spracúva. Faktúru vám pošleme e-mailom hneď po potvrdení. Túto stránku môžete zavrieť.
            </p>
          </div>
        )}

        {state === "cancelled" && (
          <div data-testid="payment-cancelled" className="space-y-5">
            <XCircle className="mx-auto text-white/50" size={52} />
            <h1 className="font-display text-2xl">Platba zrušená</h1>
            <p className="text-white/60 text-sm">Platba nebola dokončená. Ak ide o omyl, skúste to znova cez pôvodný platobný odkaz.</p>
          </div>
        )}

        {state === "failed" && (
          <div data-testid="payment-failed" className="space-y-5">
            <XCircle className="mx-auto text-red-400" size={52} />
            <h1 className="font-display text-2xl">Platbu sa nepodarilo overiť</h1>
            <p className="text-white/60 text-sm">Skúste obnoviť stránku. Ak problém pretrváva, kontaktujte nás na kontakt@saubereko.eu.</p>
          </div>
        )}

        <a href="/" className="inline-flex items-center gap-2 text-white/40 hover:text-[#D4AF37] text-sm mt-12 transition-colors" data-testid="payment-home-link">
          <ArrowLeft size={15} /> Späť na úvod
        </a>
      </div>
    </div>
  );
}
